import type { Answers, Condition, FieldType, LocationValue, Operator, Rule, RuleGroup } from './types';

export function isGroup(rule: Rule): rule is RuleGroup {
  return typeof rule === 'object' && rule !== null && !('field' in rule);
}

export function isEmptyValue(v: unknown): boolean {
  if (v === undefined || v === null) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    // ranges: {min,max} / {from,to} — empty when every bound is empty
    const keys = Object.keys(o);
    if (keys.length === 0) return true;
    if (keys.every((k) => ['min', 'max', 'from', 'to'].includes(k))) {
      return keys.every((k) => isEmptyValue(o[k]));
    }
  }
  return false;
}

function toNumber(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return undefined;
}

function toComparable(v: unknown): number | string | undefined {
  const n = toNumber(v);
  if (n !== undefined) return n;
  if (typeof v === 'string' && v !== '') return v; // ISO dates compare lexicographically
  return undefined;
}

function asArray(v: unknown): unknown[] {
  if (Array.isArray(v)) return v;
  if (v === undefined || v === null) return [];
  return [v];
}

function eqLoose(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  const na = toNumber(a);
  const nb = toNumber(b);
  if (na !== undefined && nb !== undefined) return na === nb;
  if (typeof a === 'boolean' || typeof b === 'boolean') return String(a) === String(b);
  return false;
}

function locationIn(actual: unknown, expected: unknown): boolean {
  if (!actual || typeof actual !== 'object') return false;
  const a = actual as LocationValue;
  const targets = asArray(expected) as Partial<LocationValue>[];
  return targets.some((t) => {
    if (!t || typeof t !== 'object') return false;
    if (t.neighborhoodId != null) return eqLoose(a.neighborhoodId, t.neighborhoodId);
    if (t.areaId != null) return eqLoose(a.areaId, t.areaId);
    if (t.governorateId != null) return eqLoose(a.governorateId, t.governorateId);
    if (t.countryId != null) return eqLoose(a.countryId, t.countryId);
    return false;
  });
}

/** Evaluate one condition against the (already cleaned) answers. */
export function evalCondition(c: Condition, answers: Answers): boolean {
  const actual = answers[c.field];
  const empty = isEmptyValue(actual);

  switch (c.op as Operator) {
    case 'is_empty':
      return empty;
    case 'is_not_empty':
      return !empty;
    case 'eq':
      return !empty && eqLoose(actual, c.value);
    case 'neq':
      return empty || !eqLoose(actual, c.value);
    case 'in':
      return !empty && asArray(c.value).some((x) => eqLoose(actual, x));
    case 'not_in':
      return empty || !asArray(c.value).some((x) => eqLoose(actual, x));
    case 'contains':
      return asArray(actual).some((x) => eqLoose(x, c.value));
    case 'not_contains':
      return !asArray(actual).some((x) => eqLoose(x, c.value));
    case 'contains_any': {
      const arr = asArray(actual);
      return asArray(c.value).some((v) => arr.some((x) => eqLoose(x, v)));
    }
    case 'contains_all': {
      const arr = asArray(actual);
      const wanted = asArray(c.value);
      return wanted.length > 0 && wanted.every((v) => arr.some((x) => eqLoose(x, v)));
    }
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      const a = toComparable(actual);
      const b = toComparable(c.value);
      if (a === undefined || b === undefined || typeof a !== typeof b) return false;
      if (c.op === 'gt') return a > b;
      if (c.op === 'gte') return a >= b;
      if (c.op === 'lt') return a < b;
      return a <= b;
    }
    case 'between': {
      const a = toComparable(actual);
      const [lo, hi] = asArray(c.value).map(toComparable);
      if (a === undefined || lo === undefined || hi === undefined) return false;
      if (typeof a !== typeof lo || typeof a !== typeof hi) return false;
      return a >= lo && a <= hi;
    }
    case 'location_in':
      return locationIn(actual, c.value);
    default:
      return false;
  }
}

/** Evaluate a rule tree. `null`/`undefined` means "always true". */
export function evalRule(rule: Rule | null | undefined, answers: Answers): boolean {
  if (rule === null || rule === undefined) return true;
  if (!isGroup(rule)) return evalCondition(rule, answers);

  let result = true;
  if (rule.all) result = result && rule.all.every((r) => evalRule(r, answers));
  if (rule.any) result = result && (rule.any.length === 0 || rule.any.some((r) => evalRule(r, answers)));
  if (rule.not) result = result && !evalRule(rule.not, answers);
  return result;
}

/** Flatten every condition inside a rule tree. */
export function collectConditions(rule: Rule | null | undefined): Condition[] {
  if (!rule) return [];
  if (!isGroup(rule)) return [rule];
  return [
    ...(rule.all ?? []).flatMap(collectConditions),
    ...(rule.any ?? []).flatMap(collectConditions),
    ...collectConditions(rule.not),
  ];
}

// ---------------------------------------------------------------------------
// Operator ↔ field-type compatibility
// ---------------------------------------------------------------------------
const EMPTY_OPS: Operator[] = ['is_empty', 'is_not_empty'];
const SINGLE_CHOICE_OPS: Operator[] = ['eq', 'neq', 'in', 'not_in', ...EMPTY_OPS];
const MULTI_OPS: Operator[] = ['contains', 'contains_any', 'contains_all', 'not_contains', ...EMPTY_OPS];
const ORDERED_OPS: Operator[] = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', ...EMPTY_OPS];
const TEXT_OPS: Operator[] = ['eq', 'neq', 'in', 'not_in', ...EMPTY_OPS];

export const OPERATORS_BY_TYPE: Record<FieldType, Operator[]> = {
  text: TEXT_OPS,
  textarea: EMPTY_OPS,
  number: ORDERED_OPS,
  currency: ORDERED_OPS,
  number_range: EMPTY_OPS,
  phone: EMPTY_OPS,
  email: EMPTY_OPS,
  date: ORDERED_OPS,
  date_range: EMPTY_OPS,
  boolean: ['eq', 'neq', ...EMPTY_OPS],
  radio: SINGLE_CHOICE_OPS,
  dropdown: SINGLE_CHOICE_OPS,
  request_type: SINGLE_CHOICE_OPS,
  property_category: SINGLE_CHOICE_OPS,
  property_type: SINGLE_CHOICE_OPS,
  checkbox_group: MULTI_OPS,
  property_features: MULTI_OPS,
  image_upload: EMPTY_OPS,
  file_upload: EMPTY_OPS,
  location: ['location_in', ...EMPTY_OPS],
  map_point: EMPTY_OPS,
  section: [],
  info: [],
};
